import { Dispatch, SetStateAction, createContext, useCallback, useContext, useEffect, useMemo, useState, } from "react";
import { useAuth } from "react-oidc-context";
import { useConfig } from '../../config/ConfigProvider';
import { components } from '../../models/api/v1';
import { buildUrl, downloadData, getData } from "../../services/api/apiCall";
import { SUBSCRIPTION_GET_BRFS } from "../../services/constants/ApiPathnames";

type MaintenancePlan = components["schemas"]["MaintenancePlanResponseModel"];

export interface UserDataInterface {
    aud: string;
    contact_id: string;
    exp: number;
    family_name: string;
    given_name: string;
    iat: number;
    idp: string;
    iss: string;
    name: string;
    sid: string;
    sub: string;
    user_settings_url: string;
}

export interface AccountInterface {
    accountName: string;
    accountStatus: AccountStatusInterface;
    companyRegistrationNumber: string;
    id: string;
    invoiceInformation: {
        invoiceAddress: InvoiceInfoInterface;
    };
    onboardingSteps: number[];
    subscription: AccountSubInterface;
    users: [
        {
            firstName: string;
            id: string;
            lastName: string;
        }
    ];
}

export interface MaintenancePlanInterface {
    data: MaintenancePlan | null;
    isError: boolean;
    isLoading: boolean;
}
export interface EngagementInterface {
    communityCode: string;
    name: string;
}

export interface DataCompletenessInterface {
    isComplete: boolean;
    reason: string;
}
interface AccountStatusInterface {
    id: number;
    value: string;
}
interface AccountSubInterface {
    cancellationDate: string | null;
    endDate: string | null;
    startDate: string | null;
}
interface InvoiceInfoInterface {
    addressRow1: string | null;
    addressRow2: string | null;
    careOf: string | null;
    city: string | null;
    country: string | null;
    zipCode: string | null;
}

interface EngagementsInterface {
    name: string;
    communityCode: string;
}

interface BrfInterface {
    accounts: AccountInterface[];
    engagements: EngagementsInterface[];
    firstName: string;
    id: string;
    lastName: string;
    isAdmin: boolean;
}

interface BRFDataInterface {
    data: BrfInterface | null;
    isLoading: boolean;
    isError: boolean;
    isValidUser: boolean;
}

interface UserInterface {
    access_token: string;
    expires_at: number;
    id_token: string;
    profile: UserDataInterface;
    refresh_token: string;
    scope: string;
    session_state: string;
    state: any;
    token_type: string;
}

interface PropertyDataInterface {
    data: any;
    isError: boolean;
    isLoading: boolean;
}

interface PdfPreviewData {
    overview: {
      accountName: string;
      startYear: number;
      yearsIncluded: number;
      lastUpdated: string;
    };
    costSummary: {
      totalCost: number;
      costByYear: Record<number, number>;
    };
    actionsByYear: Array<{
      year: number;
      yearTotalCost: number;
      actions: Array<PdfMaintenanceActionItem>;
    }>;
    allActions: Array<PdfMaintenanceActionItem>;
  }

  interface PdfMaintenanceActionItem {
    year: number;
    name: string;
    activityCode: string;
    category: string;
    affiliation: string;
    interval?: number;
    lastPerformedYear?: number;
    quantity: string;
    status: string;
    classification: string;
    comment: string;
    cost?: number;
  }
  
  interface PdfGenerationRequest {
    htmlContent: string;
    title: string;
    fileName?: string;
    pdfOptions?: {
      orientation: string;
      pageSize: string;
      metadata?: {
        author: string;
        subject: string;
        keywords: string;
      };
    };
  }

type SlimNodeResponseModel = components["schemas"]["SlimNodeResponseModel"];
type UserResponseModel = components["schemas"]["UserResponseModel"];
interface AuthContextType {
    userData: UserInterface;
    userProfile: UserDataInterface;
    brfData: BRFDataInterface;
    getBRFData: () => Promise<UserResponseModel>;
    setBrfData: Dispatch<SetStateAction<BRFDataInterface>>;
    propertyData: PropertyDataInterface;
    maintenancePlan: MaintenancePlanInterface;
    sequencePlan: any;
    getPropertyData: () => Promise<any>;
    isDownloadingStatus: boolean;
    fastighetImage: any;
    getFastighetImage: () => Promise<any>;
    getMaintenancePlan: () => Promise<void>;
    getSequencePlan: () => Promise<void>;
    getNodes: () => Promise<void>;
    selectedBRF: AccountInterface | null | undefined;
    selectedEngagement: EngagementInterface | null | undefined;
    setSelectedBRF: Dispatch<SetStateAction<AccountInterface | undefined | null>>;
    setSelectedEngagement: Dispatch<SetStateAction<EngagementsInterface | undefined | null>>;
    handleMaintenancePlanCSVDownload: () => Promise<void>;
    handleMaintenancePlanExcelDownload: () => Promise<void>;
    handleActionListCSVDownload: () => Promise<void>;
    handleActionListExcelDownload: () => Promise<void>;
    isValidUser: boolean;
    allNodes: SlimNodeResponseModel[] | undefined;
    selectedCategories: string[];
    selectedClassifications: string[];
    selectedNodes: string[];
    selectedStatus: string;
    viewType: string;
    dataType: string;
    setSelectedCategories: Dispatch<SetStateAction<string[]>>;
    setSelectedClassifications: Dispatch<SetStateAction<string[]>>;
    setSelectedNodes: Dispatch<SetStateAction<string[]>>;
    setSelectedStatus: Dispatch<SetStateAction<string>>;
    setViewType: Dispatch<SetStateAction<string>>;
    setDataType: Dispatch<SetStateAction<string>>;
    isPdfLoading: boolean;
    isPdfExporting: boolean;
    pdfError: string | null;
    getPdfPreviewData: () => Promise<PdfPreviewData | null>;
    handlePdfExport: (request: PdfGenerationRequest) => Promise<void>;
    dataCompleteness: DataCompletenessInterface;
    setDataCompleteness: Dispatch<SetStateAction<DataCompletenessInterface>>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

interface AuthProviderProps {
    children: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
    const config = useConfig(); 
    const [userData, setUserData] = useState<any>();
    const [userProfile, setUserProfile] = useState<any>();
    const [isValidUser] = useState<boolean>(false); // TODO: Används denna alls? Ingen referens till settern
    const [brfData, setBrfData] = useState<BRFDataInterface>({
        data: null,
        isLoading: true,
        isError: false,
        isValidUser: false,
    });
    const [selectedBRF, setSelectedBRF] = useState<AccountInterface | null>();
    const [selectedEngagement, setSelectedEngagement] =
        useState<EngagementsInterface | null>();
    const [propertyData, setPropertyData] = useState<any>({
        data: null,
        isLoading: true,
        isError: false,
    });
    const [maintenancePlan, setMaintenancePlan] = useState<MaintenancePlanInterface>({
        data: null,
        isLoading: true,
        isError: false,
    });
    const [sequencePlan, setSequencePlan] = useState<any>({
        data: null,
        isLoading: true,
        isError: false,
    });

    const [fastighetImage, setFastighetImage] = useState<any>();
    const [isDownloadingStatus, setIsDownloadingStatus] = useState<boolean>(false);
    const [allNodes, setAllNodes] = useState<SlimNodeResponseModel[]>();
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [selectedClassifications, setSelectedClassifications] = useState<string[]>([]);
    const [selectedNodes, setSelectedNodes] = useState<string[]>([]);
    const [selectedStatus, setSelectedStatus] = useState<string>("All");
    const [viewType, setViewType] = useState<string>("year");
    const [dataType, setDataType] = useState<string>("fiveYear");
    const [isPdfLoading, setIsPdfLoading] = useState<boolean>(false);
    const [isPdfExporting, setIsPdfExporting] = useState<boolean>(false);
    const [pdfError, setPdfError] = useState<string | null>(null);
    const [dataCompleteness, setDataCompleteness] = useState<DataCompletenessInterface>({
        isComplete: false,
        reason: "",
    });
    const auth = useAuth();

    const getBRFData = useCallback(async () => {
        const contactID = { ...userProfile }.contact_id ?? { ...userProfile }.employee_id;
        
        if (contactID && userData.access_token) {
            const data = await getData(SUBSCRIPTION_GET_BRFS(contactID), "mnt", userData.access_token)
                .then((result) => {
                    if (!result || result.error) {
                        setBrfData((initial) => {
                            return { ...initial, isError: true, isLoading: false };
                        });
                        return;
                    }
    
                    const { response } = result;
                    if (response) {
                        return response;
                    }
    
                    return;
                })
                .then((data) => {
                    return data;
                });
    
            if (data) {
                const sortedData = {
                    ...data,
                    accounts: data.accounts ? [...data.accounts].sort((a, b) => 
                        a.accountName.localeCompare(b.accountName, 'sv-SE')
                    ) : []
                };

                setBrfData((initial) => {
                    return {
                        ...initial,
                        data: sortedData,
                        isLoading: false,
                        isValidUser: true,
                    };
                });
            }
            return data && data;
        }

        setBrfData((initial) => {
            return { ...initial, isError: true, isLoading: false };
        });
    }, [userData, userProfile]);

    const getPropertyData = useCallback(async () => {
        if (selectedBRF) {
            const response = await getData(
                `community/${selectedBRF.id}/property`,
                "mnt",
                userData.access_token
            )
                .then((res: any) => {
                    const { response, error } = res;
                    if (error) {
                        setPropertyData((initial: any) => {
                            return { ...initial, isError: true, isLoading: false };
                        });
                        return;
                    }
                    if (response) {
                        return response;
                    }
                    return;
                })
                .then((data) => {
                    return data;
                });
            if (response) {
                setPropertyData((initial: any) => {
                    return { ...initial, data: response, isLoading: false };
                });
                return response && response;
            }
        }
        return;
    }, [selectedBRF, userData]);

    const checkDataCompleteness = useCallback(async () => {
        if (!selectedBRF) {
            return;
        }

        const response = await getData(
            `community/${selectedBRF.id}/property/check-data-completeness`,
            'mnt',
            userData.access_token
        ).then((result) => {
            if (result && result.response && !result.error) {
                return result.response as DataCompletenessInterface;
            }
        })

        if (response) {
            setDataCompleteness(response);
        }
    }, [selectedBRF, userData]);

    const getMaintenancePlan = useCallback(async () => {
        if (!selectedBRF) {
            return;
        }
  
        const data = await getData(
            `community/${selectedBRF.id
            }/maintenance-plan?startYear=${new Date().getFullYear()}&api-version=2`,
            "mnt",
            userData.access_token
        ).then((result) => {
            if(result && result.response && !result.error) {
                return result.response as MaintenancePlan;
            }
        });

        if (data) {
            setMaintenancePlan((initial) => ({ ...initial, data, isLoading: false }));
        }
    }, [userData, selectedBRF, setMaintenancePlan]);

    const getSequencePlan = useCallback(async () => {
        if (selectedBRF) {
            const data = await getData(
                `community/${selectedBRF.id}/maintenance-plan/sequence`,
                "mnt",
                userData.access_token
            )
                .then((res: any) => {
                    const { response } = res;
                    return response && response;
                })
                .then((data) => {
                    return data && data;
                });
            data &&
                setSequencePlan((initial: any) => {
                    return { ...initial, data: data, isLoading: false };
                });
        }
    }, [userData, selectedBRF, setSequencePlan]);

    const getFastighetImage = useCallback(async () => {
        if (selectedBRF) {
            const data = await getData(
                `account/${selectedBRF.id}/image`,
                "mnt",
                userData.access_token
            )
                .then((res: any) => {
                    const { response } = res;
                    return response && response;
                })
                .then((data) => {
                    return data && data;
                });

            data && setFastighetImage(data);
        }
    }, [selectedBRF, userData]);

    const getNodes = useCallback(async () => {
        if (selectedBRF) {
            const data = await getData(
                `/community/${selectedBRF.id}/property/allnodes`,
                "mnt",
                userData.access_token
            )
                .then((result) => {
                    return result?.response && result.response;
                })
                .then((data) => {
                    return data && data;
                });

            data && setAllNodes(data);
        }
    }, [selectedBRF, userData]);

    const getUserData = useCallback(async () => {
        if (auth.user) {
            const uData = { ...auth.user };
            setUserData(uData);
            setUserProfile(uData.profile);
        }
    }, [auth]);

    const handleMaintenancePlanCSVDownload = useCallback(async () => {
        setIsDownloadingStatus(true);
        if (selectedBRF) {
            const fileName = `Underhållsplan-${selectedBRF.companyRegistrationNumber
                }-${new Date().getUTCDate()}`;

            await downloadData(
                `community/${selectedBRF.id}/maintenance-plan/csv`,
                "mnt",
                userData.access_token,
                fileName
            );
            setIsDownloadingStatus(false);
        }
    }, [selectedBRF, userData, setIsDownloadingStatus]);

    const handleMaintenancePlanExcelDownload = useCallback(async () => {
        setIsDownloadingStatus(true);
        if (selectedBRF) {
            const fileName = `Underhållsplan-${selectedBRF.companyRegistrationNumber}-${new Date().getUTCDate()}`;
    
            await downloadData(
                `community/${selectedBRF.id}/maintenance-plan/excel`,
                "mnt",
                userData.access_token,
                fileName
            );
            setIsDownloadingStatus(false);
        }
    }, [selectedBRF, userData, setIsDownloadingStatus]);

    const handleActionListCSVDownload = useCallback(async () => {
        setIsDownloadingStatus(true);
        if (selectedBRF) {
            const fileName = `Lista-av-atgarder-${selectedBRF.companyRegistrationNumber}-${new Date().getUTCDate()}`;
    
            await downloadData(
                `community/${selectedBRF.id}/maintenance-plan/actions/csv`,
                "mnt",
                userData.access_token,
                fileName
            );
            setIsDownloadingStatus(false);
        }
    }, [selectedBRF, userData, setIsDownloadingStatus]);
    
    const handleActionListExcelDownload = useCallback(async () => {
        setIsDownloadingStatus(true);
        if (selectedBRF) {
            const fileName = `Lista-av-atgarder-${selectedBRF.companyRegistrationNumber}-${new Date().getUTCDate()}`;
    
            await downloadData(
                `community/${selectedBRF.id}/maintenance-plan/actions/excel`,
                "mnt",
                userData.access_token,
                fileName
            );
            setIsDownloadingStatus(false);
        }
    }, [selectedBRF, userData, setIsDownloadingStatus]);

    const getPdfPreviewData = useCallback(async (): Promise<PdfPreviewData | null> => {
        if (!selectedBRF) {
            setPdfError('No community selected');
            return null;
        }
        
        setIsPdfLoading(true);
        setPdfError(null);
        
        try {
            const data = await getData(
                `community/${selectedBRF.id}/maintenance-plan/pdf-preview`,
                "mnt",
                userData.access_token
            );
            
            if (!data || data.error) {
                setPdfError('Failed to fetch PDF preview data');
                return null;
            }
            
            return data.response;
        } catch (err) {
            setPdfError(`Error fetching PDF preview data: ${err instanceof Error ? err.message : String(err)}`);
            return null;
        } finally {
            setIsPdfLoading(false);
        }
    }, [selectedBRF, userData]);
    
    const handlePdfExport = useCallback(async (request: PdfGenerationRequest): Promise<void> => {
        if (!selectedBRF) {
          setPdfError('No community selected');
          return;
        }
        
        setIsPdfExporting(true);
        setPdfError(null);
        
        try {
          const url = `/community/${selectedBRF.id}/maintenance-plan/pdf`;
          
          const response = await fetch(buildUrl(config.mntBaseUrl, url), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/pdf',
              'Authorization': `Bearer ${userData.access_token}`
            },
            body: JSON.stringify(request)
          });
          
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to download PDF: ${errorText}`);
          }
          
          // Handle the blob response and trigger download
          const blob = await response.blob();
          const fileURL = URL.createObjectURL(blob);
          const fileLink = document.createElement("a");
          fileLink.href = fileURL;
          fileLink.download = `${request.fileName || request.title.replace(/\s+/g, '_')}.pdf`;
          document.body.appendChild(fileLink);
          fileLink.click();
          document.body.removeChild(fileLink);
          URL.revokeObjectURL(fileURL);
          
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : String(err);
          setPdfError(`Error generating PDF: ${errorMessage}`);
        } finally {
          setIsPdfExporting(false);
        }
      }, [selectedBRF, userData, setPdfError, setIsPdfExporting]);

    useEffect(() => {
        if (selectedBRF) {
          localStorage.setItem('brf', selectedBRF.id);
        }
      }, [selectedBRF]);

    useEffect(() => {
        getUserData();
    }, [getUserData]);

    useEffect(() => {
        userProfile && getBRFData();
    }, [getBRFData, userProfile]);

    useEffect(() => {
        if (selectedBRF && userData) {
            getPropertyData();
        }
    }, [selectedBRF, getPropertyData, userData]);

    useEffect(() => {
        if (userData && userData.access_token && selectedBRF) {
            checkDataCompleteness();
            getMaintenancePlan();
            getSequencePlan();
            getFastighetImage();
            getNodes();
        }
    }, [
        selectedBRF,
        getMaintenancePlan,
        getSequencePlan,
        getFastighetImage,
        userData,
        getNodes,
        checkDataCompleteness
    ]);

    const memoedValue = useMemo(
        () => ({
            userData,
            brfData,
            getBRFData,
            setBrfData,
            propertyData,
            maintenancePlan,
            sequencePlan,
            userProfile,
            getPropertyData,
            isDownloadingStatus,
            fastighetImage,
            getMaintenancePlan,
            getSequencePlan,
            getFastighetImage,
            selectedBRF,
            setSelectedBRF,
            selectedEngagement,
            setSelectedEngagement,
            isValidUser,
            handleMaintenancePlanCSVDownload,
            handleMaintenancePlanExcelDownload,
            handleActionListCSVDownload,
            handleActionListExcelDownload,
            allNodes,
            getNodes,
            selectedCategories,
            selectedClassifications,
            selectedNodes,
            selectedStatus,
            viewType,
            setSelectedCategories,
            setSelectedClassifications,
            setSelectedNodes,
            setSelectedStatus,
            setViewType,
            dataType,
            setDataType,
            isPdfLoading,
            isPdfExporting,
            pdfError,
            getPdfPreviewData,
            handlePdfExport,
            dataCompleteness,
            setDataCompleteness,
        }),
        [
            userData,
            brfData,
            getBRFData,
            setBrfData,
            propertyData,
            maintenancePlan,
            sequencePlan,
            userProfile,
            getPropertyData,
            isDownloadingStatus,
            fastighetImage,
            getMaintenancePlan,
            getSequencePlan,
            getFastighetImage,
            selectedBRF,
            setSelectedBRF,
            selectedEngagement,
            setSelectedEngagement,
            isValidUser,
            handleMaintenancePlanCSVDownload,
            handleMaintenancePlanExcelDownload,
            handleActionListCSVDownload,
            handleActionListExcelDownload,
            allNodes,
            getNodes,
            selectedCategories,
            selectedClassifications,
            selectedNodes,
            selectedStatus,
            setSelectedCategories,
            setSelectedClassifications,
            setSelectedNodes,
            setSelectedStatus,
            viewType,
            setViewType,
            dataType,
            setDataType,
            isPdfLoading,
            isPdfExporting,
            pdfError,
            getPdfPreviewData,
            handlePdfExport,
            dataCompleteness,
            setDataCompleteness,
        ]
    );

    return (
        <AuthContext.Provider value={memoedValue}>{children}</AuthContext.Provider>
    );
};

const useAuthentication = () => {
    return useContext(AuthContext);
};

// eslint-disable-next-line react-refresh/only-export-components
export default useAuthentication;
